import { useRef, useState } from "react";
import { purityToKarat } from "@/lib/card";
import { toast } from "sonner";
import { Upload, Loader2, Plus, Save, X, Download, Image as ImageIcon } from "lucide-react";
import { uploadImage, errMsg, assetUrl } from "@/lib/api";
import { groupFields } from "@/components/data/fieldGroups";

const ImageField = ({ k, value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 h-10 rounded-md bg-slate-800 overflow-hidden grid place-items-center shrink-0">
        {value ? <img src={assetUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-slate-500" />}
      </div>
      <button type="button" onClick={() => ref.current?.click()} className="cf-btn-ghost h-9 text-xs flex-1" data-testid={`form-upload-${k}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {value ? "Replace" : "Upload"}
      </button>
      {value && <button type="button" onClick={() => onChange("")} className="cf-btn-ghost h-9 px-2" data-testid={`form-clear-${k}`}><X size={14} /></button>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`form-file-${k}`} />
    </div>
  );
};

export default function RecordForm({ fields, form, setForm, editing, onSave, onCancel, onSample, autoKeys }) {
  const hasKarat = fields.some((f) => f.key === "KARAT");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "PURITY" && hasKarat ? { KARAT: purityToKarat(v) } : {}) }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4 self-start" data-testid="record-form">
      <div className="flex items-center justify-between">
        <p className="font-heading font-bold">{editing ? "Edit card" : "Add a card"}</p>
        {onSample && <button type="button" onClick={onSample} className="text-[11px] text-slate-400 hover:text-amber-300 flex items-center gap-1" data-testid="download-sample-csv">
          <Download size={12} /> Sample CSV
        </button>}
      </div>
      {fields.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="no-fields-message">
          This design has no data fields. Add <span className="font-mono text-indigo-300">{"{{FIELD}}"}</span> placeholders or image fields in the editor to enable data merge.
        </p>
      ) : (
        <div className="space-y-5" data-testid="record-form-sections">
          {groupFields(fields).map((g) => (
            <section key={g.id} className="rounded-lg border overflow-hidden" style={{ borderColor: `${g.color}55` }} data-testid={`form-section-${g.id}`}>
              <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ background: `${g.color}1F`, borderColor: `${g.color}55` }}>
                <g.icon size={14} style={{ color: g.color }} />
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: g.color }}>{g.title}</p>
                <span className="ml-auto text-[10px] font-mono text-slate-500">{g.fields.length}</span>
              </div>
              <div className="divide-y divide-slate-700/70">
                {g.fields.map((f, i) => (
                  <label key={f.key} className={`grid grid-cols-[42%_1fr] items-center gap-3 px-3 py-2 ${i % 2 ? "bg-slate-800/70" : "bg-slate-950/70"}`}>
                    <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: g.color }}>{f.label || f.key}</span>
                    {f.type === "image" ? (
                      <ImageField k={f.key} value={form[f.key]} onChange={(v) => set(f.key, v)} />
                    ) : (
                      <input className={`cf-input h-9 text-sm bg-slate-900 border-slate-600 focus:border-amber-400 ${autoKeys?.includes(f.key) ? "font-mono text-amber-300 cursor-not-allowed" : "text-white"}`} value={form[f.key] ?? ""}
                        readOnly={autoKeys?.includes(f.key)} placeholder={autoKeys?.includes(f.key) ? "Auto" : ""} title={autoKeys?.includes(f.key) ? "Auto-generated on save" : undefined}
                        onChange={(e) => set(f.key, e.target.value)} data-testid={`form-field-${f.key}`} />
                    )}
                  </label>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button className="cf-btn-primary h-10 flex-1 text-sm" disabled={!fields.length} data-testid="record-save-button">
          {editing ? <><Save size={15} /> Update card</> : <><Plus size={15} /> Add card</>}
        </button>
        {(editing || Object.keys(form).length > 0) && (
          <button type="button" onClick={onCancel} className="cf-btn-ghost h-10 text-sm" data-testid="record-cancel-button">Clear</button>
        )}
      </div>
    </form>
  );
}
